import { Mail, Globe, FileText, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import MsEssentials from "@/assets/images/hosting/Microsoft-Essentials.png";
import MsProfessional from "@/assets/images/hosting/Microsoft-Professional.png";

export default function PricingPlans() {
	const navigate = useNavigate();

	const handleGetStarted = () => {
		navigate('/request-for-services');
	};

	const plans = [
		{
			title: "Microsoft 365 Email Essentials",
			description: "Professional email for small teams",
			image: MsEssentials,
			features: [
				{
					icon: Mail,
					text: "10 GB business email mailbox"
				},
				{
					icon: Globe,
					text: "Custom domain email address"
				},
				{
					icon: Users,
					text: "Webmail access from any device"
				}
			],
			recommended: false
		},
		{
			title: "Microsoft 365 Email Plus",
			description: "Expanded email storage for growing teams",
			image: MsEssentials,
			features: [
				{
					icon: Mail,
					text: "50 GB business email mailbox"
				},
				{
					icon: Globe,
					text: "Custom domain email address"
				},
				{
					icon: Users,
					text: "Shared calendars & contacts"
				}
			],
			recommended: false
		},
		{
			title: "Microsoft 365 Business Essentials",
			description: "Email and essential productivity tools",
			image: MsProfessional,
			features: [
				{
					icon: Mail,
					text: "50 GB professional email"
				},
				{
					icon: Globe,
					text: "Custom domain email"
				},
				{
					icon: FileText,
					text: "Web versions of Word, Excel & PowerPoint"
				},
				{
					icon: Users,
					text: "Microsoft Teams for chat & meetings"
				}
			],
			recommended: true
		},
		{
			title: "Microsoft 365 Business Professional",
			description: "Full productivity suite with desktop apps",
			image: MsProfessional,
			features: [
				{
					icon: Mail,
					text: "50 GB secure business email"
				},
				{
					icon: Globe,
					text: "Custom domain email"
				},
				{
					icon: FileText,
					text: "Desktop Office apps (up to 5 devices)"
				},
				{
					icon: Users,
					text: "Microsoft Teams with online meetings"
				}
			],
			recommended: false
		}
	];

	return (
		<div className="bg-[#1a1a1a] py-20 lg:py-24" id="pricing-section-emailhosting">
			<div className="container">
				{/* Header */}
				<div className="text-center mb-16">
					<span className="inline-flex items-center gap-2 px-4 py-2 mb-6 bg-white/5 rounded-full border border-white/10 backdrop-blur-sm text-xs md:text-sm font-semibold text-gray-300 tracking-wider uppercase">
						Pricing Plans
					</span>
					<h3 className="text-3xl md:text-4xl font-bold text-white leading-tight mb-4">
						Flexible plans built for every business
					</h3>
					<p className="text-gray-300 md:text-lg max-w-3xl mx-auto">
						Pick the Microsoft 365 plan that fits your team and workflow
					</p>
				</div>

				{/* Pricing Cards */}
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-8 lg:gap-6 max-w-7xl mx-auto items-stretch">
					{plans.map((plan, index) => (
						<div key={index} className="relative h-full">
							{/* Recommended Badge */}
							{plan.recommended && (
								<div className="absolute -top-4 left-0 right-0 z-10 flex justify-center">
									<div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-2 rounded-full text-xs font-bold uppercase tracking-wide shadow-lg">
										Recommended
									</div>
								</div>
							)}

							<div
								className={`h-full flex flex-col bg-gradient-to-br from-gray-900/50 to-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border ${plan.recommended
										? 'border-purple-500/50 shadow-2xl shadow-purple-500/20'
										: 'border-purple-500/20'
									} hover:border-purple-500/50 transition-all duration-300`}
							>
								{/* Plan Image */}
								<div className="mb-6 h-[160px] flex items-center justify-center">
									<img
										src={plan.image}
										alt={plan.title}
										className="w-full h-full object-cover rounded-lg"
									/>
								</div>

								{/* Title */}
								<h2 className="text-white text-xl font-bold mb-3 leading-snug min-h-[56px] text-center">
									{plan.title}
								</h2>

								{/* Description */}
								<p className="text-gray-300 text-sm text-center mb-6">
									{plan.description}
								</p>

								{/* Divider */}
								<div className="h-px bg-gradient-to-r from-transparent via-purple-500/30 to-transparent mb-6" />

								{/* Features */}
								<div className="flex-grow">
									<ul className="space-y-4">
										{plan.features.map((feature, idx) => {
											const Icon = feature.icon;
											return (
												<li key={idx} className="flex items-start gap-3">
													<div className="flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 flex items-center justify-center mt-0.5">
														<Icon className="w-3 h-3 text-white" strokeWidth={2.5} />
													</div>
													<span className="text-gray-300 text-sm leading-relaxed">
														{feature.text}
													</span>
												</li>
											);
										})}
									</ul>
								</div>
							</div>
						</div>
					))}
				</div>

				{/* CTA Button - Now outside the cards */}
				<div className="text-center mt-14 mb-3">
					<button
						onClick={handleGetStarted}
						className="group inline-flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white font-semibold py-4 px-8 rounded-lg transition-all duration-300 shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 active:scale-[0.98]"
					>
						Get Started with Microsoft 365
						{/* <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" /> */}
					</button>
					<p className="text-gray-300 mt-8 italic">
						*Choose your plan and start transforming your business communication
					</p>
				</div>

				{/* Footer Note */}
				{/* <div className="text-center mt-12">
					<p className="text-gray-500 text-sm">
						Looking for a custom plan?{" "}
						<a
							href="/contact-us"
							className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400 hover:from-purple-300 hover:to-blue-300 transition-all font-medium"
						>
							Contact us
						</a>{" "}
						for a solution tailored to your business
					</p>
				</div> */}
			</div>
		</div>
	);
}